import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, formatErr } from "../lib/auth";
import api from "../lib/api";
import TopNav from "../components/TopNav";
import RequestAccessModal from "../components/RequestAccessModal";
import { ShieldPlus, Building2, UserPlus, ShieldCheck, Eye } from "lucide-react";

const TRACKS = [
  { id: "operator", name: "Operator" },
  { id: "engineer", name: "Engineer" },
  { id: "hunter", name: "Hunter / Analyst" },
  { id: "leader", name: "Leader" },
];

export default function Register() {
  const { register, demoLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteCode = params.get("invite") || "";
  const [showReq, setShowReq] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", track: "operator", org_name: "",
  });
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteErr, setInviteErr] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Resolve the invite (if any) — public endpoint that gives us the org name + role.
  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${inviteCode}/preview`);
        if (!cancelled) {
          setInvitePreview(data);
          setForm((f) => ({
            ...f,
            track: data.track || f.track,
            email: data.email_locked || f.email,
          }));
        }
      } catch (e) {
        if (!cancelled) setInviteErr(formatErr(e?.response?.data?.detail) || "Invite not valid.");
      }
    })();
    return () => { cancelled = true; };
  }, [inviteCode]);

  const mode = useMemo(() => (inviteCode ? "invite" : "org"), [inviteCode]);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        track: form.track,
      };
      if (mode === "invite") {
        payload.invite_code = inviteCode;
      } else if (form.org_name.trim()) {
        payload.org_name = form.org_name.trim();
      }
      await register(payload);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  // Invite present but invalid — block the form entirely.
  if (inviteCode && inviteErr) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex flex-col">
        <TopNav landing />
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md flat-card p-8" data-testid="invite-invalid">
            <div className="overline mb-2">// INVITE_FAILED</div>
            <h1 className="font-display font-bold text-2xl uppercase">Invite not valid</h1>
            <p className="mt-3 text-sm text-[var(--text-2)]">{inviteErr}</p>
            <button onClick={() => setShowReq(true)} className="btn-ghost mt-6 inline-flex items-center gap-2" data-testid="invite-invalid-request">
              <ShieldCheck size={14} /> Request Access
            </button>
            <RequestAccessModal open={showReq} onClose={() => setShowReq(false)} />
          </div>
        </main>
      </div>
    );
  }

  // No invite at all — registration is closed. Show the gate.
  if (!inviteCode) {
    const startDemo = async () => {
      if (demoLoading) return;
      setDemoLoading(true);
      try {
        const u = await demoLogin();
        nav(u?.is_demo ? "/assistant?demo=1" : "/dashboard");
      } catch (e2) {
        console.error("demo-login failed", e2);
        setDemoLoading(false);
      }
    };
    return (
      <div className="min-h-screen bg-[var(--bg)] flex flex-col">
        <TopNav landing />
        <RequestAccessModal open={showReq} onClose={() => setShowReq(false)} />
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md flat-card p-8" data-testid="register-locked">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck size={22} color="var(--cyan)" strokeWidth={1.5} />
              <div className="overline">// INVITE_ONLY</div>
            </div>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mb-2">By invitation only</h1>
            <p className="text-sm text-[var(--text-2)] mb-6">
              DefDossier is sales-led. New squadrons are provisioned manually so we can match the right pricing and onboarding to your team. Walk the live demo, then talk to us.
            </p>
            <div className="space-y-3">
              <button onClick={startDemo} disabled={demoLoading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="locked-demo">
                <Eye size={16} /> {demoLoading ? "Loading demo…" : "Try the Live Demo"}
              </button>
              <button onClick={() => setShowReq(true)} className="btn-ghost w-full inline-flex items-center justify-center gap-2" data-testid="locked-request">
                <ShieldCheck size={16} /> Request Full Access
              </button>
            </div>
            <div className="mt-6 text-sm text-[var(--text-2)]">
              Already enlisted? <Link to="/login" className="text-[var(--cyan)] hover:underline" data-testid="locked-to-login">Login</Link>
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
              Have an invite link? Click the link in your invitation email.
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <TopNav landing />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md flat-card p-8 scanlines" data-testid="register-card">
          {mode === "invite" ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <UserPlus size={22} color="var(--cyan)" strokeWidth={1.5} />
                <div className="overline">// JOIN_SQUAD</div>
              </div>
              <h1 className="font-display font-bold text-3xl uppercase tracking-tight mb-1">Join {invitePreview?.org_name || "the squad"}</h1>
              <p className="text-sm text-[var(--text-2)] mb-6" data-testid="invite-summary">
                You're joining as <span className="text-white">{invitePreview?.role || "trainee"}</span> on the <span className="text-[var(--cyan)]">{invitePreview?.track || form.track}</span> track.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2">
                <Building2 size={22} color="var(--cyan)" strokeWidth={1.5} />
                <div className="overline">// START_PILOT</div>
              </div>
              <h1 className="font-display font-bold text-3xl uppercase tracking-tight mb-1">Start 30-day pilot</h1>
              <p className="text-sm text-[var(--text-2)] mb-6">
                Spin up a squadron for your defence contractor. 10 seats, full platform, zero card on file.
              </p>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "org" && (
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Organisation</label>
                <input value={form.org_name} onChange={upd("org_name")} className="input-tac mt-1" placeholder="ACME Defence Systems" data-testid="reg-org-name" />
                <p className="font-mono text-[10px] text-[var(--text-2)] mt-1">Optional — defaults to your own squadron.</p>
              </div>
            )}
            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Name</label>
              <input required value={form.name} onChange={upd("name")} className="input-tac mt-1" placeholder="Sgt. Jane Doe" data-testid="reg-name" />
            </div>
            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Email</label>
              <input type="email" required value={form.email} onChange={upd("email")} disabled={!!invitePreview?.email_locked}
                     className="input-tac mt-1" placeholder="you@yourcompany.com" data-testid="reg-email" />
              {invitePreview?.email_locked && (
                <p className="font-mono text-[10px] text-[var(--text-2)] mt-1">Invite is locked to this address.</p>
              )}
            </div>
            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={upd("password")} className="input-tac mt-1" placeholder="min 6 characters" data-testid="reg-password" />
            </div>
            <div>
              <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Track</label>
              <select value={form.track} onChange={upd("track")} disabled={mode === "invite"} className="input-tac mt-1" data-testid="reg-track">
                {TRACKS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {mode === "invite" && (
                <p className="font-mono text-[10px] text-[var(--text-2)] mt-1">Track is set by the invite.</p>
              )}
            </div>

            {err && <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="reg-error">{err}</div>}

            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="reg-submit">
              {busy ? (mode === "invite" ? "Joining…" : "Provisioning…") : (mode === "invite" ? "Join Squad" : "Start Pilot")}
            </button>
          </form>

          <div className="mt-6 text-sm text-[var(--text-2)]">
            Already enlisted? <Link to="/login" className="text-[var(--cyan)] hover:underline" data-testid="reg-to-login">Login</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
